import { Dropdown, DropdownItem, ListItemLayout, LuneThemeNew, Text } from '@lune-fe/lune-ui-lib'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import Box from '@mui/material/Box'
import { styled } from '@mui/system'
import { flatten } from 'lodash'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'

import DiscardChangesConfirmation from 'components/DiscardChangesConfirmation'
import { setUserPrimaryAccountId } from 'endpoints/dapi'
import useAccounts from 'hooks/useAccounts'
import useAllOrganisationAccounts from 'hooks/useAllOrganisationAccounts'
import useHasUnsavedChanges from 'hooks/useHasUnsavedChanges'
import useLogout from 'hooks/useLogout'
import useOrganisations from 'hooks/useOrganisations'
import { IAccount } from 'models/account'
import { queryKeys } from 'queryKeys'
import { SnackbarMessages } from 'SnackbarMessages'

const AccountSwitcher = () => {
    const { palette, spacing } = LuneThemeNew
    const queryClient = useQueryClient()
    const { logout } = useLogout()
    const { hasUnsavedChanges, setHasUnsavedChangesState } = useHasUnsavedChanges()
    const [openModal, setOpenModal] = useState<boolean>(false)

    const { activeAccount } = useAccounts()

    const [accounts, setAccounts] = useState<IAccount[]>([])
    const { refetching, paginatedAccounts, hasNextPage, fetchNextPage } =
        useAllOrganisationAccounts({
            limit: 100,
        })

    // go through all pages: programmatically trigger `fetchNextPage()`
    useEffect(() => {
        if (refetching) {
            return
        }

        if (hasNextPage) {
            fetchNextPage()
        } else {
            setAccounts(flatten((paginatedAccounts?.pages ?? []).map(({ data }) => data)))
        }
        // The eslint exception is required to avoid having to pass `fetchNextPage` in the dependency array
        // because it causes infinite loops
    }, [paginatedAccounts?.pages.length, hasNextPage, refetching]) // eslint-disable-line react-hooks/exhaustive-deps

    const { enqueueSnackbar: snackbar } = useSnackbar()
    const { organisations } = useOrganisations()

    const [newAccount, setNewAccount] = useState<{
        accId: string
        orgId: string
    }>({
        accId: '',
        orgId: '',
    })

    const changeAccount = (id: string, orgId: string) => {
        const activeAccOrgId = activeAccount?.organisationId
        if (id === activeAccount?.id) {
            return
        }
        setUserPrimaryAccountId(id as any as number).then(() => {
            if (orgId === activeAccOrgId) {
                queryClient.invalidateQueries({
                    predicate: (query) => query.queryKey !== queryKeys.GET_ACCOUNTS,
                })
            } else {
                queryClient.invalidateQueries()
            }
            snackbar(SnackbarMessages.ACCOUNT_CHANGE)
        })
    }

    const switchAccount = (id: string, orgId: string) => {
        if (hasUnsavedChanges) {
            setNewAccount({ accId: id, orgId })
            setOpenModal(true)
        } else {
            changeAccount(id, orgId)
        }
    }

    const StyledHr = styled('hr')(({ theme }) =>
        theme.unstable_sx({
            border: 0,
            height: '1px',
            width: 1,
            background: palette.Grey300,
            margin: 0,
        }),
    )

    const MENU_MIN_WIDTH = '330px'
    const MENU_MAX_HEIGHT = '480px'
    const MENU_LEFT_OFFSET = '250px'

    return (
        <>
            <Dropdown
                dataTestId={`account-switch-dropdown`}
                buttonLabel={
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            height: spacing(7),
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                flexDirection: 'column',
                                width: '100%',
                            }}
                        >
                            <Text
                                variant={'body3'}
                                sx={{
                                    fontSize: '16px',
                                    lineHeight: '21px',
                                }}
                            >
                                {activeAccount?.name ?? ''}
                            </Text>
                            <Text
                                variant={'body3'}
                                sx={{
                                    color: palette.TextSecondary,
                                    lineHeight: '17px',
                                }}
                            >
                                Switch account
                            </Text>
                        </Box>
                        <ChevronRightIcon sx={{ color: palette.TextSecondary }} />
                    </Box>
                }
                compact
                menuAutoWidth
                sx={{
                    width: '100%',
                    height: spacing(7),
                    backgroundColor: palette.Surface300,
                    borderRadius: '12px',
                    '.MuiButton-icon': {
                        display: 'none',
                    },
                    '.MuiTypography-root': {
                        width: '100%',
                    },
                    '&[aria-expanded="true"]': {
                        backgroundColor: palette.Surface400,
                    },
                    '&:hover': {
                        background: palette.Surface400,
                    },
                }}
                paperSx={{
                    minWidth: MENU_MIN_WIDTH,
                    maxHeight: MENU_MAX_HEIGHT,
                    ml: MENU_LEFT_OFFSET,
                    mt: spacing(-7),
                    '.MuiList-root': {
                        pt: 0,
                        pb: 0,
                    },
                }}
            >
                {organisations.map((org) => {
                    const organisationAccounts = accounts.filter((a) => a.organisationId === org.id)
                    return [
                        <Box
                            key={`account-switch-organisation-${org.id}`}
                            sx={{
                                padding: `0 ${spacing(2)}`,
                                borderBottom: `1px solid ${palette.Grey300}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: 2,
                                height: '68px',
                            }}
                        >
                            <CorporateFareOutlinedIcon sx={{ fill: palette.TextSecondary }} />
                            <Box>
                                <Text variant={'body2'}>{org.name}</Text>
                                <Text
                                    variant={'caption'}
                                    sx={{
                                        color: palette.TextSecondary,
                                    }}
                                >
                                    {organisationAccounts.length} accounts
                                </Text>
                            </Box>
                        </Box>,
                        [
                            organisationAccounts.map((acc) => (
                                <DropdownItem
                                    selected={acc.id === activeAccount?.id}
                                    onClick={() => switchAccount(acc.id, acc.organisationId)}
                                    key={`account-switch-account-${acc.id}`}
                                    sx={{
                                        pl: 2,
                                        pr: 2,
                                        height: spacing(6),
                                    }}
                                >
                                    <Text variant={'body3'}>{acc.name || ''}</Text>
                                </DropdownItem>
                            )),
                        ],
                        <StyledHr key={`StyledHr`} />,
                    ]
                })}
                <DropdownItem
                    data-testid={`logout-btn`}
                    sx={{
                        pl: 2,
                        pr: 2,
                        height: spacing(6),
                    }}
                    onClick={() => logout()}
                >
                    <ListItemLayout compact image={<LogoutIcon />} title={'Logout'} />
                </DropdownItem>
            </Dropdown>
            {openModal && (
                <DiscardChangesConfirmation
                    onConfirm={() => {
                        setOpenModal(false)
                        setHasUnsavedChangesState(false)
                        changeAccount(newAccount.accId, newAccount.orgId)
                    }}
                    onCancel={() => {
                        setOpenModal(false)
                    }}
                />
            )}
        </>
    )
}

export default AccountSwitcher
